import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Send, ChevronLeft, ChevronRight, Bell, Clock, Phone, Pencil, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";

export default function CalendarPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const locale = i18n.language === "es" ? "es-ES" : "it-IT";
  const [appts, setAppts] = useState([]);
  const [patients, setPatients] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [month, setMonth] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(new Date());
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ patient_id: "", treatment: "", date: "", time: "10:00", notes: "", duration_min: 60, consent_template_id: "", type: "trattamento" });

  // Normalize catalog: strings become {name, duration_min}
  const catalog = (user?.treatments || []).map((x) =>
    typeof x === "string" ? { name: x, duration_min: user?.slot_duration || 30 } : x
  );

  function onTreatmentChange(v) {
    // If value matches a catalog item, autofill its duration; otherwise keep the current duration.
    const match = catalog.find((c) => (c.name || "").toLowerCase() === (v || "").toLowerCase());
    if (match) setForm((f) => ({ ...f, treatment: v, duration_min: match.duration_min || f.duration_min }));
    else setForm((f) => ({ ...f, treatment: v }));
  }

  const load = async () => {
    const [a, p, tmp] = await Promise.all([api.get("/appointments"), api.get("/patients"), api.get("/consent-templates")]);
    setAppts(a.data); setPatients(p.data); setTemplates(tmp.data);
  };
  useEffect(() => { load(); }, []);

  async function create(e) {
    e.preventDefault();
    if (!form.patient_id || !form.treatment || !form.date) { toast.error(t("calendar.form.completeFields")); return; }
    const iso = new Date(`${form.date}T${form.time}:00`).toISOString();
    try {
      await api.post("/appointments", { ...form, date: iso, duration_min: Number(form.duration_min) });
      toast.success(t("calendar.form.created"));
      setShowNew(false); setForm({ patient_id: "", treatment: "", date: "", time: "10:00", notes: "", duration_min: 60, consent_template_id: "", type: "trattamento" });
      load();
    } catch (e) { toast.error(apiError(e)); }
  }

  async function del(id) {
    if (!window.confirm(t("calendar.confirmDelete"))) return;
    await api.delete(`/appointments/${id}`); load();
  }

  async function sendReminder(a) {
    try {
      const { data } = await api.post(`/appointments/${a.id}/send-reminder`);
      const portalUrl = `${window.location.origin}${data.portal_path || `/cita/${a.reminder_token || ""}`}`;
      const phone = (a.patient_phone || "").replace(/[\s-]/g, "").replace("+", "");
      const dt = new Date(a.date);
      const datePretty = dt.toLocaleDateString(locale, { day: "2-digit", month: "long" }) + " " + dt.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
      const msg = `Ciao ${a.patient_name}, ${a.treatment} · ${datePretty} → ${portalUrl}`;
      window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, "_blank");
      toast.success(t("calendar.openingWhatsapp"));
      load();
    } catch (e) { toast.error(apiError(e)); }
  }

  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const startWeekday = (first.getDay() + 6) % 7;
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(month.getFullYear(), month.getMonth(), d));

  function apptsOn(date) {
    if (!date) return [];
    return appts.filter((a) => {
      const ad = new Date(a.date);
      return ad.getFullYear() === date.getFullYear() && ad.getMonth() === date.getMonth() && ad.getDate() === date.getDate();
    });
  }

  const upcomingList = appts.filter((a) => new Date(a.date).getTime() >= Date.now() - 86400000)
    .sort((a, b) => new Date(a.date) - new Date(b.date));

  const remindersDue = upcomingList.filter((a) => {
    if (a.reminder_sent) return false;
    const diff = (new Date(a.date).getTime() - Date.now()) / 86400000;
    return diff <= 3 && diff >= 0;
  });

  return (
    <div className="space-y-8 fade-in-up">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-stone-500 mb-3">{t("calendar.eyebrow")}</p>
          <h1 className="font-heading text-5xl font-light">{t("calendar.title")}</h1>
        </div>
        <button onClick={() => setShowNew(!showNew)} data-testid="btn-new-appointment"
          className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#D4A373] text-stone-900 hover:bg-[#B07D4F] hover:text-white transition-colors font-medium">
          <Plus className="w-4 h-4" /> {t("calendar.newAppointment")}
        </button>
      </div>

      {remindersDue.length > 0 && (
        <div className="bg-[#1C1917] text-white rounded-2xl p-6 flex items-start gap-4" data-testid="reminders-alert">
          <Bell className="w-5 h-5 text-[#D4A373] mt-1" strokeWidth={1.5} />
          <div className="flex-1">
            <p className="text-xs uppercase tracking-[0.2em] text-stone-400 mb-2">{t("calendar.remindersPending")}</p>
            <div className="space-y-2">
              {remindersDue.map((a) => (
                <div key={a.id} className="flex items-center justify-between gap-4 py-2 border-b border-stone-800 last:border-0">
                  <div className="text-sm">
                    <span className="font-medium">{a.patient_name}</span>
                    <span className="text-stone-400"> · {a.treatment} · {new Date(a.date).toLocaleString(locale, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                  </div>
                  <button onClick={() => sendReminder(a)} data-testid={`send-reminder-${a.id}`}
                    className="flex items-center gap-1 px-4 py-1.5 rounded-full bg-[#D4A373] text-stone-900 text-xs font-medium hover:bg-white">
                    <Send className="w-3 h-3" /> WhatsApp
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {showNew && (
        <form onSubmit={create} className="bg-white border border-stone-200 rounded-2xl p-8 space-y-4" data-testid="new-appointment-form">
          <h3 className="font-heading text-2xl font-light">{t("calendar.form.title")}</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{t("calendar.form.patient")} *</label>
              <select value={form.patient_id} onChange={(e) => setForm({ ...form, patient_id: e.target.value })} required data-testid="appt-patient"
                className="w-full px-4 py-3 bg-[#FAF9F6] border border-stone-200 rounded-xl">
                <option value="">{t("calendar.form.selectPatient")}</option>
                {patients.map((p) => <option key={p.id} value={p.id}>{p.full_name} · {p.phone}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{t("calendar.form.apptType")} *</label>
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} required data-testid="appt-type"
                className="w-full px-4 py-3 bg-[#FAF9F6] border border-stone-200 rounded-xl">
                <option value="trattamento">{t("dashboard.typeTreatment")}</option>
                <option value="controllo">{t("dashboard.typeControl")}</option>
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{t("calendar.form.treatment")} *</label>
              <input value={form.treatment} onChange={(e) => onTreatmentChange(e.target.value)} required data-testid="appt-treatment"
                list="calendar-treatments-datalist" autoComplete="off"
                placeholder={catalog.length ? catalog[0].name : ""}
                className="w-full px-4 py-3 bg-[#FAF9F6] border border-stone-200 rounded-xl" />
              <datalist id="calendar-treatments-datalist">
                {catalog.map((c) => (
                  <option key={c.name} value={c.name}>{c.duration_min} min</option>
                ))}
              </datalist>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{t("common.date")} *</label>
              <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required data-testid="appt-date"
                className="w-full px-4 py-3 bg-[#FAF9F6] border border-stone-200 rounded-xl" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{t("common.time")} *</label>
              <input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} required data-testid="appt-time"
                className="w-full px-4 py-3 bg-[#FAF9F6] border border-stone-200 rounded-xl" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{t("calendar.form.duration")}</label>
              <input type="number" min="1" step="1" value={form.duration_min}
                onChange={(e) => setForm({ ...form, duration_min: e.target.value })}
                data-testid="appt-duration"
                className="w-full px-4 py-3 bg-[#FAF9F6] border border-stone-200 rounded-xl" />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{t("calendar.form.consentTemplate")}</label>
              <select value={form.consent_template_id} onChange={(e) => setForm({ ...form, consent_template_id: e.target.value })}
                data-testid="appt-template"
                className="w-full px-4 py-3 bg-[#FAF9F6] border border-stone-200 rounded-xl">
                <option value="">{t("calendar.form.noConsent")}</option>
                {templates.map((tp) => <option key={tp.id} value={tp.id}>{tp.treatment} · {tp.original_filename}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{t("common.notes")}</label>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2}
              spellCheck={true} autoCorrect="on" autoCapitalize="sentences"
              className="w-full px-4 py-3 bg-[#FAF9F6] border border-stone-200 rounded-xl" />
          </div>
          <div className="flex gap-3">
            <button type="submit" data-testid="submit-appointment" className="px-6 py-2.5 rounded-full bg-stone-900 text-white hover:bg-stone-800">{t("common.save")}</button>
            <button type="button" onClick={() => setShowNew(false)} className="px-6 py-2.5 rounded-full border border-stone-200">{t("common.cancel")}</button>
          </div>
        </form>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-stone-200 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-heading text-2xl font-light">
              {month.toLocaleDateString(locale, { month: "long", year: "numeric" })}
            </h3>
            <div className="flex gap-2">
              <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1))} className="p-2 rounded-full border border-stone-200 hover:bg-stone-50" data-testid="prev-month">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button onClick={() => setMonth(new Date())} className="px-4 py-1.5 text-xs uppercase tracking-widest rounded-full border border-stone-200 hover:bg-stone-50">{t("common.today")}</button>
              <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1))} className="p-2 rounded-full border border-stone-200 hover:bg-stone-50" data-testid="next-month">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="grid grid-cols-7 gap-1 mb-2">
            {["L", "M", "X", "J", "V", "S", "D"].map((d) => (
              <div key={d} className="text-center text-xs uppercase tracking-widest text-stone-400 py-2">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {cells.map((date, i) => {
              const items = apptsOn(date);
              const today = date && date.toDateString() === new Date().toDateString();
              return (
                <div key={i} onClick={() => date && setSelectedDay(date)}
                  data-testid={date ? `day-${date.toISOString().slice(0,10)}` : undefined}
                  className={`min-h-[80px] p-2 rounded-lg text-xs ${date ? "border cursor-pointer" : ""} ${
                    date && selectedDay && date.toDateString() === selectedDay.toDateString()
                      ? "border-[#8FA08B] bg-[#8FA08B]/10 ring-2 ring-[#8FA08B]/40"
                      : today ? "bg-[#D4A373]/10 border-[#D4A373]/40" : (date ? "border-stone-100 hover:bg-stone-50" : "")
                  }`}>
                  {date && (
                    <>
                      <div className={`font-medium mb-1 ${today ? "text-[#B07D4F]" : "text-stone-700"}`}>{date.getDate()}</div>
                      {items.slice(0, 2).map((a) => (
                        <div key={a.id} className="truncate bg-[#D4A373] text-stone-900 px-1.5 py-0.5 rounded mb-0.5" title={`${a.patient_name} - ${a.treatment}`}>
                          {new Date(a.date).toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" })} {a.patient_name}
                        </div>
                      ))}
                      {items.length > 2 && <div className="text-stone-500">+{items.length - 2}</div>}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-white border border-stone-200 rounded-2xl p-6">
          {/* Day view header with prev/next arrows */}
          <div className="flex items-center justify-between mb-4">
            <button data-testid="prev-day"
              onClick={() => setSelectedDay(new Date(selectedDay.getFullYear(), selectedDay.getMonth(), selectedDay.getDate() - 1))}
              className="p-2 rounded-full border border-stone-200 hover:bg-stone-50">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="text-center flex-1 mx-2">
              <p className="text-[10px] uppercase tracking-[0.25em] text-stone-500">{t("calendar.dayView") || "Giornata"}</p>
              <h3 className="font-heading text-xl font-light capitalize">
                {selectedDay.toLocaleDateString(locale, { weekday: "long", day: "2-digit", month: "long" })}
              </h3>
            </div>
            <button data-testid="next-day"
              onClick={() => setSelectedDay(new Date(selectedDay.getFullYear(), selectedDay.getMonth(), selectedDay.getDate() + 1))}
              className="p-2 rounded-full border border-stone-200 hover:bg-stone-50">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          <div className="space-y-3 stagger max-h-[560px] overflow-y-auto">
            {(() => {
              const dayAppts = apptsOn(selectedDay).sort((a, b) => new Date(a.date) - new Date(b.date));
              if (dayAppts.length === 0) {
                return <p className="text-sm text-stone-500">{t("calendar.noAppointmentsDay") || "Nessun appuntamento in questa giornata"}</p>;
              }
              return dayAppts.map((a) => (
                <div key={a.id} data-testid={`appt-item-${a.id}`} className="p-4 border border-stone-100 rounded-xl hover:border-[#D4A373]/40 transition">
                  <div className="font-medium text-stone-900">{a.patient_name}</div>
                  <div className="text-xs text-stone-500 mt-1">{a.treatment}</div>
                  <div className="flex items-center gap-1 text-xs text-stone-600 mt-2">
                    <Clock className="w-3 h-3" />
                    {new Date(a.date).toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" })} · {a.duration_min}m
                  </div>
                  {a.patient_phone && (
                    <div className="flex items-center gap-1 text-xs text-stone-500 mt-1">
                      <Phone className="w-3 h-3" /> {a.patient_phone}
                    </div>
                  )}
                  {a.notes && <div className="text-xs text-stone-600 mt-2 italic">"{a.notes}"</div>}
                  <div className="flex gap-2 mt-3 flex-wrap">
                    <button onClick={() => setEditing(a)} data-testid={`edit-${a.id}`}
                      className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-stone-900 text-white hover:bg-stone-700">
                      <Pencil className="w-3 h-3" /> {t("common.edit") || "Modifica"}
                    </button>
                    <button onClick={() => sendReminder(a)} data-testid={`remind-${a.id}`}
                      className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-[#8FA08B] text-white hover:opacity-90">
                      <Send className="w-3 h-3" /> {t("calendar.remind")}
                    </button>
                    <button onClick={() => del(a.id)} data-testid={`del-${a.id}`}
                      className="text-xs px-3 py-1.5 rounded-full border border-stone-200 text-stone-500 hover:bg-stone-50">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                  {a.reminder_sent && <div className="text-[10px] uppercase tracking-widest text-[#8FA08B] mt-2">{t("calendar.reminderSent")}</div>}
                </div>
              ));
            })()}
          </div>
        </div>
      </div>

      {editing && (
        <EditAppointmentModal
          appt={editing}
          patients={patients}
          templates={templates}
          catalog={catalog}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function EditAppointmentModal({ appt, patients, templates, catalog, onClose, onSaved }) {
  const { t } = useTranslation();
  const d = new Date(appt.date);
  const pad = (n) => String(n).padStart(2, "0");
  const [form, setForm] = useState({
    patient_id: appt.patient_id,
    treatment: appt.treatment,
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
    duration_min: appt.duration_min,
    notes: appt.notes || "",
    type: appt.type || "trattamento",
    consent_template_id: appt.consent_template_id || "",
  });
  const [busy, setBusy] = useState(false);

  function pickTreatment(v) {
    const match = catalog.find((c) => (c.name || "").toLowerCase() === (v || "").toLowerCase());
    if (match) setForm((f) => ({ ...f, treatment: v, duration_min: match.duration_min || f.duration_min }));
    else setForm((f) => ({ ...f, treatment: v }));
  }

  async function save(e) {
    e.preventDefault();
    if (!form.patient_id || !form.treatment || !form.date) { toast.error(t("calendar.form.completeFields")); return; }
    setBusy(true);
    try {
      const iso = new Date(`${form.date}T${form.time}:00`).toISOString();
      await api.put(`/appointments/${appt.id}`, {
        ...form, date: iso, duration_min: Number(form.duration_min),
      });
      toast.success(t("common.saved") || "Salvato");
      onSaved();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 bg-stone-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose} data-testid="edit-appt-overlay">
      <form onSubmit={save} onClick={(e) => e.stopPropagation()} data-testid="edit-appt-form"
        className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl">
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-stone-100">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-stone-500 mb-1">{t("common.edit") || "Modifica"}</p>
            <h3 className="font-heading text-2xl font-light">{appt.patient_name}</h3>
          </div>
          <button type="button" onClick={onClose} className="text-stone-400 hover:text-stone-900" data-testid="edit-appt-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{t("calendar.form.patient")} *</label>
            <select value={form.patient_id} onChange={(e) => setForm({ ...form, patient_id: e.target.value })} required
              data-testid="edit-appt-patient"
              className="w-full px-4 py-3 bg-[#FAF9F6] border border-stone-200 rounded-xl">
              {patients.map((p) => <option key={p.id} value={p.id}>{p.full_name} · {p.phone}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{t("calendar.form.apptType")} *</label>
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
              data-testid="edit-appt-type"
              className="w-full px-4 py-3 bg-[#FAF9F6] border border-stone-200 rounded-xl">
              <option value="trattamento">{t("dashboard.typeTreatment")}</option>
              <option value="controllo">{t("dashboard.typeControl")}</option>
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{t("calendar.form.treatment")} *</label>
            <input type="text" value={form.treatment} onChange={(e) => pickTreatment(e.target.value)} required
              data-testid="edit-appt-treatment"
              list="edit-treatments-datalist" autoComplete="off"
              className="w-full px-4 py-3 bg-[#FAF9F6] border border-stone-200 rounded-xl" />
            <datalist id="edit-treatments-datalist">
              {catalog.map((c) => <option key={c.name} value={c.name}>{c.duration_min} min</option>)}
            </datalist>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{t("calendar.form.duration")}</label>
            <input type="number" min="1" value={form.duration_min}
              onChange={(e) => setForm({ ...form, duration_min: e.target.value })}
              data-testid="edit-appt-duration"
              className="w-full px-4 py-3 bg-[#FAF9F6] border border-stone-200 rounded-xl" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{t("common.date")} *</label>
            <input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
              data-testid="edit-appt-date"
              className="w-full px-4 py-3 bg-[#FAF9F6] border border-stone-200 rounded-xl" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{t("common.time")} *</label>
            <input type="time" required value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })}
              data-testid="edit-appt-time"
              className="w-full px-4 py-3 bg-[#FAF9F6] border border-stone-200 rounded-xl" />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{t("calendar.form.consentTemplate")}</label>
            <select value={form.consent_template_id} onChange={(e) => setForm({ ...form, consent_template_id: e.target.value })}
              data-testid="edit-appt-template"
              className="w-full px-4 py-3 bg-[#FAF9F6] border border-stone-200 rounded-xl">
              <option value="">{t("calendar.form.noConsent")}</option>
              {templates.map((tp) => <option key={tp.id} value={tp.id}>{tp.treatment} · {tp.original_filename}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{t("common.notes")}</label>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3}
              data-testid="edit-appt-notes"
              spellCheck={true} autoCorrect="on" autoCapitalize="sentences"
              className="w-full px-4 py-3 bg-[#FAF9F6] border border-stone-200 rounded-xl" />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-stone-100 flex gap-3 justify-end">
          <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-full border border-stone-200 hover:bg-stone-50 text-sm">
            {t("common.cancel")}
          </button>
          <button type="submit" disabled={busy} data-testid="edit-appt-submit"
            className="px-5 py-2.5 rounded-full bg-stone-900 text-white hover:bg-stone-800 text-sm disabled:opacity-50">
            {busy ? "…" : (t("common.save") || "Salva")}
          </button>
        </div>
      </form>
    </div>
  );
}
